// screens/create-product-mgt/AllocateMenuPrice.jsx
import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Table, Card, Alert, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from '../../api/axios';
const AllocateMenuPrice = ({ data, onNext, onBack, updateData, currentStep, isSubmitting }) => {
    const [groups, setGroups] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [prices, setPrices] = useState({ mrpPrice: '', offerPrice: '', isIntroductoryFlag: false });
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [existingPricingData, setExistingPricingData] = useState([]);

    const accessToken = localStorage.getItem('authToken');
    const [pricingId, setPricingId] = useState('');
    const productData = data.product || {};
    const productId = productData._id;
    const isFreebieProduct = productData?.isFreebieProduct || false;

    const getMenuGroup = async () => {
        try {
            const response = await axiosInstance.get("menu-group", {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response?.data?.status === true) {
                setGroups(response?.data?.data?.menuGroup || []);
                fetchExistingPricing(response?.data?.data?.menuGroup || []);
            }
        } catch (error) {
            if (error.response?.data?.status === false) {
                toast.error(error.response.data.error, { position: "top-right", autoClose: 3000, theme: "colored" });
            }
        }
    };

    useEffect(() => { getMenuGroup(); }, []);

    useEffect(() => {
        if (isFreebieProduct) {
            setPrices({ mrpPrice: '0', offerPrice: '0', isIntroductoryFlag: false });
        }
    }, [isFreebieProduct]);

    const getFilteredPrices = (data, groupList) => {
        if (!groupList?.length || !data?.length) return;

        const allGroups = groupList.map((group) => {
            let existingPrice = data.find(
                (pricing) => pricing?.group?._id === group?._id && pricing?.product?._id === productId
            );
            setPricingId(existingPrice?._id);
            
            if (!existingPrice) {
                existingPrice = data.find((pricing) => pricing?.group?.isDefaultGroup === true);
            }

            return {
                ...group,
                pricingId: existingPrice?._id || null,
                groupId: group?._id,
                groupName: group?.groupName,
                mrpPrice: isFreebieProduct ? '0' : (existingPrice?.mrpPrice ?? productData?.mrp ?? ''),
                offerPrice: isFreebieProduct ? '0' : (existingPrice?.offerPrice ?? productData?.offerPrice ?? ''),
                isIntroductoryFlag: isFreebieProduct ? false : (existingPrice?.isIntroductoryFlag ?? false),
                isSelected: !!existingPrice && !isFreebieProduct,
                isFreebie: isFreebieProduct
            };
        });

        setSelectedGroups(allGroups);
    };
    
    const fetchExistingPricing = async (groupL) => {
        if (!productData._id) return;
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`menu-product-pricing?product=${productData._id}`, {
                headers: { Authorization: `Bearer ${accessToken}` }
            });
            if (response?.data?.status === true) {
                setExistingPricingData(response.data.data.menuGroup || []);
                getFilteredPrices(response.data.data.menuGroup || [], groupL)
            }
        } catch (error) {
            console.log("Error fetching existing pricing:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handlePriceChange = (field, value) => {
        if (isFreebieProduct) return;
        setPrices(prev => ({ ...prev, [field]: value }));
    };

    const handleGroupSelection = (groupId, isSelected) => {
        if (isFreebieProduct) return;
        setSelectedGroups(prev => prev.map(group =>
            group._id === groupId ? { ...group, isSelected } : group
        ));
    };

    const handleGroupPriceChange = (groupId, field, value) => {
        if (isFreebieProduct) return;
        setSelectedGroups(prev => prev.map(group =>
            group._id === groupId ? { ...group, [field]: value } : group
        ));
    };

    const handleGroupIntroductoryFlagChange = (groupId, isChecked) => {
        if (isFreebieProduct) return;
        setSelectedGroups(prev => prev.map(group =>
            group._id === groupId ? { ...group, isIntroductoryFlag: isChecked } : group
        ));
    };

    const applyUniformPrice = () => {
        if (isFreebieProduct) {
            toast.error('Cannot modify prices for freebie products', { position: 'top-right' });
            return;
        }
        if (!prices.mrpPrice && !prices.offerPrice && !prices.isIntroductoryFlag) {
            toast.error('Please enter at least one value to apply', { position: 'top-right' });
            return;
        }

        setSelectedGroups(prev => prev.map(group => ({
            ...group,
            mrpPrice: group.isSelected && prices.mrpPrice ? prices.mrpPrice : group.mrpPrice,
            offerPrice: group.isSelected && prices.offerPrice ? prices.offerPrice : group.offerPrice,
            isIntroductoryFlag: group.isSelected ? prices.isIntroductoryFlag : group.isIntroductoryFlag
        })));
        toast.success('Applied prices to selected groups', { position: 'top-right' });
    };

    const applyToAll = () => {
        if (isFreebieProduct) {
            toast.error('Cannot modify prices for freebie products', { position: 'top-right' });
            return;
        }
        if (!prices.mrpPrice && !prices.offerPrice && !prices.isIntroductoryFlag) {
            toast.error('Please enter at least one value to apply', { position: 'top-right' });
            return;
        }

        setSelectedGroups(prev => prev.map(group => ({
            ...group,
            mrpPrice: prices.mrpPrice || group.mrpPrice,
            offerPrice: prices.offerPrice || group.offerPrice,
            isIntroductoryFlag: prices.isIntroductoryFlag,
            isSelected: true
        })));
        toast.success('Applied prices to all groups', { position: 'top-right' });
    };

    const toggleAllSelection = (isSelected) => {
        if (isFreebieProduct) return;
        setSelectedGroups(prev => prev.map(group => ({ ...group, isSelected })));
    };

    const handleNextStep = async () => {
        if (!productData._id) {
            toast.error('Product not created yet. Please go back to Step 1.', { position: 'top-right' });
            return;
        }

        const selected = selectedGroups?.filter(group => group.isSelected || isFreebieProduct);

        if (selected.length === 0) {
            toast.error('Please select at least one menu group', { position: 'top-right' });
            return;
        }

        const invalidGroups = selected.filter(group =>
            !group.mrpPrice || group.mrpPrice < 0 || !group.offerPrice || group.offerPrice < 0
        );

        if (invalidGroups.length > 0) {
            toast.error('Please enter valid prices for all selected groups', { position: 'top-right' });
            return;
        }

        const payload = selected?.map(group => ({
            _id: group?.pricingId,
            group: group._id,
            product: productData._id,
            mrpPrice: isFreebieProduct ? 0 : parseFloat(group.mrpPrice || 0),
            offerPrice: isFreebieProduct ? 0 : parseFloat(group.offerPrice || 0),
            isIntroductoryFlag: isFreebieProduct ? false : (group.isIntroductoryFlag || false)
        }));

        setIsSaving(true);
        try {
            const response = await axiosInstance.put('menu-product-pricing/addMultileProductsWithOutFile', payload, {
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` }
            });

            if (response?.status === 200 || response?.data?.status === true) {
                const responseData = response.data.data || response.data;
                toast.success('Prices updated successfully!', { position: 'top-right' });
                updateData('pricing', {
                    menuGroups: selected,
                    payload: payload,
                    totalGroups: selected?.length,
                    isAllocated: true,
                    existingPricing: responseData
                });
                onNext();
            } else {
                toast.error(response?.data?.error || 'Failed to update prices', { position: 'top-right' });
            }
        } catch (error) {
            console.error('Error updating prices:', error);
            let errorMessage = 'Failed to update prices';
            if (error.response) {
                if (error.response.status === 406) errorMessage = error.response.data.error?.message || error.response.data.error;
                else if (error.response.status === 404) errorMessage = error.response.data.data?.message;
                else if (error.response.status === 412) errorMessage = error.response.data.data?.message;
                else if (error.response.status === 400) errorMessage = error.response.data.error;
                else if (error.response.status === 500) errorMessage = error.response.data.message || error.response.data.error;
            }
            toast.error(errorMessage, { position: "top-right", autoClose: 3000, theme: "colored" });
        } finally {
            setIsSaving(false);
        }
    };

    const selectedCount = selectedGroups.filter(g => g.isSelected).length;
    const totalGroups = groups.length;
    const hasExistingPricing = selectedGroups.some(g => g.pricingId);
    const hasAnyPriceValue = prices.mrpPrice || prices.offerPrice || prices.isIntroductoryFlag;

    return (
        <div className="allocate-menu-price-step" style={{ fontSize: '13px' }}>
            <h6 className="mb-3 text-primary">Step 2: Allocate Menu Group Pricing</h6>

            {!productData._id && (
                <Alert variant="warning" className="mb-3 py-2">
                    <small>Product not created yet. Please go back to Step 1 and create the product first to get the Product ID.</small>
                </Alert>
            )}

            {isFreebieProduct && (
                <Alert variant="info" className="mb-3 py-2">
                    <small><strong>Freebie Product:</strong> All prices automatically set to ₹0 and cannot be modified.</small>
                </Alert>
            )}

            {/* Quick Price Update */}
            <Card className="mb-3">
                <Card.Body className="py-2 px-3">
                    <h6 className="mb-2">Quick Price Update</h6>
                    <Row className="g-2 align-items-end">
                        <Col xs={6} md={2}>
                            <Form.Label className="mb-1 small">Uniform MRP (₹)</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                size="sm"
                                value={prices.mrpPrice}
                                onChange={(e) => handlePriceChange('mrpPrice', e.target.value)}
                                placeholder={productData.mrp || "MRP"}
                                disabled={isFreebieProduct}
                            />
                        </Col>
                        <Col xs={6} md={2}>
                            <Form.Label className="mb-1 small">Offer Price (₹)</Form.Label>
                            <Form.Control
                                type="number"
                                step="0.01"
                                size="sm"
                                value={prices.offerPrice}
                                onChange={(e) => handlePriceChange('offerPrice', e.target.value)}
                                placeholder={productData.offerPrice || "Offer"}
                                disabled={isFreebieProduct}
                            />
                        </Col>
                        <Col xs={6} md={2}>
                            <Form.Check
                                type="checkbox"
                                id="uniformIntroductoryFlag"
                                label={<small>Introductory</small>}
                                checked={prices.isIntroductoryFlag}
                                onChange={(e) => handlePriceChange('isIntroductoryFlag', e.target.checked)}
                                disabled={isFreebieProduct}
                                className="mt-3"
                            />
                        </Col>
                        <Col xs={6} md={6}>
                            <div className="d-flex gap-2 mt-3">
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={applyUniformPrice}
                                    disabled={isFreebieProduct || !hasAnyPriceValue}
                                >
                                    Apply to Selected
                                </Button>
                                <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={applyToAll}
                                    disabled={isFreebieProduct || !hasAnyPriceValue}
                                >
                                    Apply to All
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>

            {/* Menu Groups Pricing Table */}
            <Card>
                <Card.Body className="p-2">
                    <div className="d-flex justify-content-between align-items-center mb-2 px-2">
                        <div>
                            <h6 className="mb-0">Menu Groups Pricing</h6>
                            <small className="text-muted">
                                {selectedCount}/{totalGroups} groups selected
                                {hasExistingPricing && (
                                    <Badge bg="info" className="ms-1" style={{ fontSize: '10px' }}>
                                        {selectedGroups.filter(g => g.pricingId).length} priced
                                    </Badge>
                                )}
                                {isFreebieProduct && (
                                    <Badge bg="warning" className="ms-1" style={{ fontSize: '10px' }}>
                                        Freebie ₹0
                                    </Badge>
                                )}
                            </small>
                        </div>
                        <Form.Check
                            type="switch"
                            id="select-all-switch"
                            label={<small>All</small>}
                            checked={selectedCount === totalGroups && totalGroups > 0}
                            onChange={(e) => toggleAllSelection(e.target.checked)}
                            disabled={isFreebieProduct}
                        />
                    </div>

                    {isLoading ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" variant="primary" size="sm" />
                            <p className="mt-1 small">Loading...</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table striped hover size="sm" className="mb-0" style={{ fontSize: '12px' }}>
                                <thead>
                                    <tr>
                                        <th width="40">
                                            <Form.Check
                                                type="checkbox"
                                                checked={selectedCount === totalGroups && totalGroups > 0}
                                                onChange={(e) => toggleAllSelection(e.target.checked)}
                                                disabled={isFreebieProduct}
                                            />
                                        </th>
                                        <th>Menu Group</th>
                                        <th width="100">MRP (₹)</th>
                                        <th width="100">Offer (₹)</th>
                                        <th width="80">Intro</th>
                                        <th width="70">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedGroups.map((group) => (
                                        <tr key={group._id}>
                                            <td>
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={group.isSelected || false}
                                                    onChange={(e) => handleGroupSelection(group._id, e.target.checked)}
                                                    disabled={isFreebieProduct}
                                                />
                                            </td>
                                            <td>
                                                <strong className='text-secondary'>{group.groupName}</strong>
                                                {group.description && (
                                                    <div className="text-muted" style={{ fontSize: '10px' }}>{group.description}</div>
                                                )}
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    size="sm"
                                                    value={group.mrpPrice || ''}
                                                    onChange={(e) => handleGroupPriceChange(group._id, 'mrpPrice', e.target.value)}
                                                    disabled={!group.isSelected || isFreebieProduct}
                                                    style={{ fontSize: '12px' }}
                                                    placeholder={isFreebieProduct ? "0" : "MRP"}
                                                />
                                            </td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    size="sm"
                                                    value={group.offerPrice || ''}
                                                    onChange={(e) => handleGroupPriceChange(group._id, 'offerPrice', e.target.value)}
                                                    disabled={!group.isSelected || isFreebieProduct}
                                                    style={{ fontSize: '12px' }}
                                                    placeholder={isFreebieProduct ? "0" : "Offer"}
                                                />
                                            </td>
                                            <td className="text-center">
                                                <Form.Check
                                                    type="checkbox"
                                                    checked={group.isIntroductoryFlag || false}
                                                    onChange={(e) => handleGroupIntroductoryFlagChange(group._id, e.target.checked)}
                                                    disabled={!group.isSelected || isFreebieProduct}
                                                />
                                            </td>
                                            <td>
                                                {group.pricingId ? (
                                                    <Badge bg="success" style={{ fontSize: '9px' }}>✓</Badge>
                                                ) : (
                                                    <Badge bg="secondary" style={{ fontSize: '9px' }}>-</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}

                    <Alert variant="info" className="mt-2 mb-0 py-2" style={{ fontSize: '12px' }}>
                        <small>
                            {isFreebieProduct ? (
                                'Freebie product - all prices are set to ₹0 automatically.'
                            ) : (
                                'Select groups, modify prices, and use quick update buttons. Click Next to save and proceed.'
                            )}
                        </small>
                    </Alert>
                </Card.Body>
            </Card>

            <div className="mt-3 d-flex justify-content-between">
                <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={onBack}
                    disabled={isSaving || isSubmitting}
                >
                    ← Back
                </Button>
                <Button
                    variant="primary"
                    size="sm"
                    onClick={handleNextStep}
                    disabled={isSaving || isSubmitting || !productData._id}
                >
                    {isSaving ? (
                        <>
                            <Spinner size="sm" animation="border" className="me-1" />
                            Saving...
                        </>
                    ) : (
                        'Next: Add-ons →'
                    )}
                </Button>
            </div>
        </div>
    );
};

export default AllocateMenuPrice;